import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Divider,
  Typography,
  CircularProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { createUser, fetchUsers } from "../../../../redux/slices/user";

interface CreateContactModalProps {
  open: boolean;
  onClose: () => void;
  onContactCreated: (userId: number) => void;
  associationId: number;
}

export const CreateContactModal: React.FC<CreateContactModalProps> = ({
  open,
  onClose,
  onContactCreated,
  associationId,
}) => {
  const dispatch = useAppDispatch();
  const [creatingContact, setCreatingContact] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    invoiceEmail: "",
    deliveryMethod: "EMAIL" as "EMAIL" | "POST",
    street: "",
    postalCode: "",
    city: "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      alert("Täytä vähintään etunimi, sukunimi ja sähköposti");
      return;
    }

    // Address is always mandatory
    if (!formData.street || !formData.postalCode || !formData.city) {
      alert("Postiosoite on pakollinen");
      return;
    }

    setCreatingContact(true);
    try {
      const userData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || "",
        password: Math.random().toString(36).slice(-8), // Generate random password
        invoiceEmail: formData.invoiceEmail || null,
        invoiceDeliveryMethod: formData.deliveryMethod,
        roles: ["GUEST"],
        address: {
          street: formData.street,
          postalCode: formData.postalCode,
          city: formData.city,
        },
      };

      await dispatch(createUser(userData, associationId));

      // Refresh users list
      const response = await dispatch(fetchUsers({ associationId })).unwrap();

      // Find the newly created user
      const newUser = response.find((u: any) => u.email === formData.email);

      if (newUser) {
        onContactCreated(newUser.id);
      }

      // Reset form and close modal
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        invoiceEmail: "",
        deliveryMethod: "EMAIL",
        street: "",
        postalCode: "",
        city: "",
      });
      onClose();
    } catch (error: any) {
      console.error("Failed to create contact:", error);
      alert(
        `Kontaktin luonti epäonnistui: ${error.message || "Tuntematon virhe"}`
      );
    } finally {
      setCreatingContact(false);
    }
  };

  const handleCancel = () => {
    if (!creatingContact) {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        invoiceEmail: "",
        deliveryMethod: "EMAIL",
        street: "",
        postalCode: "",
        city: "",
      });
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Lisää uusi kontakti</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Etunimi"
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sukunimi"
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Sähköposti"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Puhelinnumero"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={creatingContact}>
                <FormLabel component="legend">Laskun toimitustapa</FormLabel>
                <RadioGroup
                  row
                  value={formData.deliveryMethod}
                  onChange={(e) =>
                    handleChange(
                      "deliveryMethod",
                      e.target.value as "EMAIL" | "POST"
                    )
                  }
                >
                  <FormControlLabel
                    value="EMAIL"
                    control={<Radio />}
                    label="Sähköposti"
                  />
                  <FormControlLabel
                    value="POST"
                    control={<Radio />}
                    label="Posti"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Laskutussähköposti"
                type="email"
                placeholder="esim. naapuri@gmail.com"
                value={formData.invoiceEmail}
                onChange={(e) => handleChange("invoiceEmail", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                helperText="Jos asetettu, laskut ja kokouskutsut lähetetään tähän osoitteeseen"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Postiosoite (pakollinen)
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Katuosoite"
                value={formData.street}
                onChange={(e) => handleChange("street", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Postinumero"
                value={formData.postalCode}
                onChange={(e) => handleChange("postalCode", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Kaupunki"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                fullWidth
                variant="outlined"
                disabled={creatingContact}
                required
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="inherit"
          disabled={creatingContact}
        >
          Peruuta
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={creatingContact}
        >
          {creatingContact ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Luo kontakti"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
